// Lecture typée de `app_config.ai_budget` (Phase C du chantier « tracking des
// dépenses IA », cf. docs/superpowers/specs/2026-07-22-tracking-phase-c-
// budget-guard-approche.md). État DÉRIVÉ écrit par le cron `budget-guard` :
// `alert` (≥ 50 $/mois) pilote la bannière admin, `suspended` (≥ 150 $/mois)
// pilote la bannière tous-usagers + le kill-switch serveur.
//
// Fail-open côté affichage aussi (symétrique à `isLlmSuspended` côté edge,
// `_shared/budget.ts`) : ni une erreur réseau/RLS, ni l'absence de row (avant
// le premier tick du cron — le seed migration couvre normalement ce cas) ne
// doivent faire planter l'écran. `{ ok: false }` reste renvoyé pour que le
// caller puisse logger/diagnostiquer, mais AUCUNE bannière ne doit dépendre
// d'un `ok: true` pour rester invisible par défaut (cf. use-ai-budget-state.ts).

import type { SupabaseClient } from "@supabase/supabase-js";

export interface AiBudgetState {
  month: string | null;
  totalCents: number;
  alert: boolean;
  suspended: boolean;
  checkedAt: string | null;
}

interface AiBudgetDbValue {
  month?: string | null;
  total_cents?: number | string | null;
  alert?: boolean | null;
  suspended?: boolean | null;
  checked_at?: string | null;
}

// bigint/numeric peuvent revenir en string selon le driver PostgREST — même
// tolérance défensive que `ai-costs/fetch.ts` (toNumber).
function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

const NEUTRAL_STATE: AiBudgetState = {
  month: null,
  totalCents: 0,
  alert: false,
  suspended: false,
  checkedAt: null,
};

export type FetchAiBudgetResult = { ok: true; data: AiBudgetState } | { ok: false; error: string };

export async function fetchAiBudgetState(client: SupabaseClient): Promise<FetchAiBudgetResult> {
  const { data, error } = await client
    .from("app_config")
    .select("value")
    .eq("key", "ai_budget")
    .maybeSingle();
  if (error) return { ok: false, error: error.message };
  if (!data) return { ok: true, data: NEUTRAL_STATE };

  const v = ((data as { value: unknown }).value ?? {}) as AiBudgetDbValue;
  return {
    ok: true,
    data: {
      month: v.month ?? null,
      totalCents: toNumber(v.total_cents),
      alert: v.alert === true,
      suspended: v.suspended === true,
      checkedAt: v.checked_at ?? null,
    },
  };
}
